/**
 * Zustand store cho Main Cafe Scene 3D.
 *
 * Wrap backend API qua `lib/backendAdapter.ts` — expose shape khớp với
 * skeleton components (CustomerType, ServeResult, SceneState).
 *
 * Khác với `shopStore.ts` (cũ — dùng cho LoginScreen / IntroScreen /
 * ShopNameScreen 2D), store này CHỈ dùng trong MainCafeScene.
 */
import { create } from "zustand";

import {
  endSessionApi,
  loadInitialState,
  serveOrderApi,
  startSession,
} from "../lib/backendAdapter";
import type {
  Customer,
  IngredientId,
  ServeResult,
  SceneState,
  CameraState,
  FloatingNumber,
} from "../types/game-3d";

interface MilestoneToastInfo {
  code: string;
  rewardBean: number;
}

interface EndSummary {
  totalOrders: number;
  totalCorrect: number;
  totalWrong: number;
  totalAbandoned: number;
  totalBeanEarned: number;
  durationSeconds: number;
}

interface Cafe3dStore {
  // Player state (sync với backend)
  beanCoin: number;
  reputation: number;
  energy: number;
  energyMax: number;
  revenueToday: number;
  lastRevenueDelta: number;
  shopName: string;
  upgrades: Record<string, number>;
  currentStreak: number;

  // Session + customer
  sessionId: string | null;
  currentCustomer: Customer | null;
  currentOrderId: string | null;
  orderSpawnedAt: number; // ms epoch
  currentMix: IngredientId[];

  // Scene state
  sceneState: SceneState;
  cameraState: CameraState;
  isRushHour: boolean;

  // Last result + UI feedback
  lastResult: ServeResult | null;
  floatingNumbers: FloatingNumber[];
  milestoneQueue: MilestoneToastInfo[];
  endSummary: EndSummary | null;
  lastError: string | null;

  // Lang
  lang: "vi" | "en";

  // Init / lifecycle
  initFromBackend: () => Promise<void>;
  reset: () => void;

  // Gameplay actions
  startNewSession: () => Promise<void>;
  addIngredient: (id: IngredientId) => void;
  removeLastIngredient: () => void;
  clearMix: () => void;
  serveCurrentOrder: () => Promise<void>;
  customerTimeout: () => Promise<void>;
  finishResultAnimation: () => void;
  endCurrentSession: () => Promise<EndSummary | null>;
  dismissEndSummary: () => void;

  // Misc
  setLang: (lang: "vi" | "en") => void;
  dismissMilestone: () => void;
  pushMilestone: (m: MilestoneToastInfo) => void;
  addFloatingNumber: (n: Omit<FloatingNumber, "id" | "createdAt">) => void;
  removeFloatingNumber: (id: string) => void;
}

export const useCafe3dStore = create<Cafe3dStore>((set, get) => ({
  beanCoin: 0,
  reputation: 0,
  energy: 5,
  energyMax: 5,
  revenueToday: 0,
  lastRevenueDelta: 0,
  shopName: "",
  upgrades: {},
  currentStreak: 0,

  sessionId: null,
  currentCustomer: null,
  currentOrderId: null,
  orderSpawnedAt: 0,
  currentMix: [],

  sceneState: "idle",
  cameraState: "idle",
  isRushHour: false,

  lastResult: null,
  floatingNumbers: [],
  milestoneQueue: [],
  endSummary: null,
  lastError: null,

  lang: "vi",

  async initFromBackend() {
    try {
      const { shop, session } = await loadInitialState();
      set({
        beanCoin: shop.beanCoin,
        reputation: shop.reputation,
        energy: shop.energy,
        energyMax: shop.energyMax,
        revenueToday: shop.revenueToday,
        shopName: shop.shopName ?? "",
        upgrades: shop.upgrades,
        currentStreak: shop.currentStreak,
        isRushHour: shop.isRushHour,
        sessionId: session.sessionId,
        currentCustomer: session.customer,
        currentOrderId: session.orderId,
        orderSpawnedAt: session.orderSpawnedAt,
        sceneState: session.customer ? "ordering" : "idle",
        cameraState: session.customer ? "customerEnter" : "idle",
        currentMix: [],
        lastError: null,
      });
    } catch (err) {
      set({
        lastError: err instanceof Error ? err.message : "Không tải được state",
      });
      throw err;
    }
  },

  reset() {
    set({
      sessionId: null,
      currentCustomer: null,
      currentOrderId: null,
      orderSpawnedAt: 0,
      currentMix: [],
      sceneState: "idle",
      cameraState: "idle",
      lastResult: null,
      floatingNumbers: [],
      milestoneQueue: [],
      endSummary: null,
      lastError: null,
    });
  },

  async startNewSession() {
    if (get().sceneState !== "idle") return;
    try {
      const { shop, session } = await startSession();
      set({
        beanCoin: shop.beanCoin,
        reputation: shop.reputation,
        energy: shop.energy,
        revenueToday: shop.revenueToday,
        upgrades: shop.upgrades,
        currentStreak: shop.currentStreak,
        isRushHour: shop.isRushHour,
        sessionId: session.sessionId,
        currentCustomer: session.customer,
        currentOrderId: session.orderId,
        orderSpawnedAt: session.orderSpawnedAt,
        sceneState: "customer_walking_in",
        cameraState: "customerEnter",
        currentMix: [],
      });
      // Sau walk-in 1.2s → state ordering.
      setTimeout(() => {
        if (get().sceneState === "customer_walking_in") {
          set({ sceneState: "ordering" });
        }
      }, 1200);
    } catch (err) {
      set({
        lastError: err instanceof Error ? err.message : "Không mở được quán",
      });
    }
  },

  addIngredient(id) {
    const state = get();
    if (state.sceneState !== "ordering") return;
    if (state.currentMix.length >= 5) return;
    // Click ingredient đầu tiên → camera zoom prepping (close shot quầy).
    // Đã ở prepping rồi thì giữ nguyên.
    const nextCameraState =
      state.currentMix.length === 0 ? "prepping" : state.cameraState;
    set({
      currentMix: [...state.currentMix, id],
      cameraState: nextCameraState,
    });
  },

  removeLastIngredient() {
    set({ currentMix: get().currentMix.slice(0, -1) });
  },

  clearMix() {
    set({ currentMix: [] });
  },

  async serveCurrentOrder() {
    const state = get();
    if (
      state.sceneState !== "ordering" ||
      !state.sessionId ||
      !state.currentOrderId ||
      state.currentMix.length === 0
    ) {
      return;
    }

    set({ sceneState: "serving", cameraState: "serving" });

    try {
      const { result, shop } = await serveOrderApi(
        state.sessionId,
        state.currentOrderId,
        state.currentMix,
      );

      const delta = result.reward.coin + result.reward.tip;
      const sceneState: SceneState =
        result.status === "correct"
          ? "success"
          : result.status === "timeout"
            ? "timeout"
            : "wrong";

      // Cinematic camera theo kết quả:
      // - correct → celebration (wide tilt-up, epic moment)
      // - wrong / timeout → serving (giữ close-up khách)
      const resultCameraState =
        result.status === "correct" ? "celebration" : "serving";

      set({
        beanCoin: shop.beanCoin,
        reputation: shop.reputation,
        revenueToday: shop.revenueToday,
        upgrades: shop.upgrades,
        lastRevenueDelta: delta,
        lastResult: result,
        sceneState,
        cameraState: resultCameraState,
        currentMix: [],
        milestoneQueue: [
          ...state.milestoneQueue,
          ...result.milestones,
        ],
      });

      // Floating number cho UI overlay.
      if (result.status === "correct") {
        get().addFloatingNumber({
          value: result.reward.coin,
          color: "#F2C94C",
          label: "Bean",
        });
        if (result.reward.tip > 0) {
          setTimeout(() => {
            get().addFloatingNumber({
              value: result.reward.tip,
              color: "#5C8A4F",
              label: "Bo",
            });
          }, 400);
        }
      } else if (result.status === "wrong") {
        get().addFloatingNumber({
          value: result.reward.coin,
          color: "#8B7355",
          label: "Bean",
        });
      }

      // Sau result animation → spawn next order từ backend response.
      // KHÔNG dùng client-side spawn (anti-cheat).
      const next = result.nextSession;
      setTimeout(() => {
        set({ sceneState: "customer_walking_out" });
        setTimeout(() => {
          if (next.customer) {
            set({
              currentCustomer: next.customer,
              currentOrderId: next.orderId,
              orderSpawnedAt: next.orderSpawnedAt,
              sceneState: "customer_walking_in",
              cameraState: "customerEnter",
              lastResult: null,
            });
            setTimeout(() => {
              if (get().sceneState === "customer_walking_in") {
                set({ sceneState: "ordering" });
              }
            }, 1200);
          } else {
            set({
              currentCustomer: null,
              currentOrderId: null,
              sceneState: "idle",
              cameraState: "idle",
              lastResult: null,
            });
          }
        }, 800);
      }, 2500);
    } catch (err) {
      // Rollback về ordering nếu network/backend fail.
      set({
        sceneState: "ordering",
        lastError: err instanceof Error ? err.message : "Lỗi phục vụ",
      });
    }
  },

  async customerTimeout() {
    // Phase 1: timed_out detect ở backend khi user serve quá patience.
    // Client cũng có thể chủ động report nếu muốn — Phase 1 skip.
    // KHÔNG implement client-side timeout (anti-cheat).
  },

  finishResultAnimation() {
    set({
      lastResult: null,
    });
  },

  async endCurrentSession() {
    const state = get();
    if (!state.sessionId) return null;
    try {
      const { summary } = await endSessionApi(state.sessionId);
      set({
        endSummary: summary,
        sessionId: null,
        currentCustomer: null,
        currentOrderId: null,
        sceneState: "idle",
        cameraState: "idle",
        currentMix: [],
      });
      // Reload shop state để energy được update từ server.
      try {
        await get().initFromBackend();
      } catch {
        // ignore
      }
      return summary;
    } catch (err) {
      set({
        lastError: err instanceof Error ? err.message : "Lỗi kết thúc ca",
      });
      return null;
    }
  },

  dismissEndSummary() {
    set({ endSummary: null });
  },

  setLang(lang) {
    set({ lang });
  },

  dismissMilestone() {
    const q = get().milestoneQueue;
    if (q.length === 0) return;
    set({ milestoneQueue: q.slice(1) });
  },

  pushMilestone(m) {
    set({ milestoneQueue: [...get().milestoneQueue, m] });
  },

  addFloatingNumber(n) {
    const id = `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    set((s) => ({
      floatingNumbers: [
        ...s.floatingNumbers,
        { ...n, id, createdAt: Date.now() },
      ],
    }));
    setTimeout(() => get().removeFloatingNumber(id), 1800);
  },

  removeFloatingNumber(id) {
    set((s) => ({
      floatingNumbers: s.floatingNumbers.filter((f) => f.id !== id),
    }));
  },
}));

export const selectHud = (s: Cafe3dStore) => ({
  beanCoin: s.beanCoin,
  reputation: s.reputation,
  energy: s.energy,
  energyMax: s.energyMax,
});
